/* eslint-disable @typescript-eslint/no-explicit-any */
type Listener = (data: any, raw?: any) => void;

interface Options {
  autoConnect?: boolean;
}

class Socket {
  ws?: WebSocket;
  private queue: Set<ActionCableMessage>;
  private listeners = new Map();
  private url: string;
  private skipReconnect = false;

  get connected() {
    return this.ws?.readyState === WebSocket.OPEN;
  }
  get connecting() {
    return this.ws?.readyState === WebSocket.CONNECTING;
  }

  constructor(url: string, options?: Options) {
    this.queue = new Set();
    this.url = url;

    if (options?.autoConnect) {
      this.open();
    }
  }

  open() {
    if (this.connected || this.connecting) return;

    this.close();

    const userId = localStorage.getItem("user") ?? "";
    const url = new URL(this.url);
    url.protocol = url.protocol === "https:" ? "wss" : "ws";
    url.searchParams.set("user_id", userId);

    this.ws = new WebSocket(url);
    this.ws.onopen = () => {
      const queue = Array.from(this.queue);
      for (const item of queue) {
        this.queue.delete(item);
        this.send(item);
      }
    };

    // forward message event to all attached handlers
    this.ws.onmessage = (event) => {
      for (const handler of this.listeners.values()) {
        handler(event);
      }
    };

    this.ws.onclose = () => {
      if (!this.skipReconnect) {
        setTimeout(() => {
          if (this.connected || this.connecting) return;
          this.open();
        }, 1_000);
      }
    };

    this.skipReconnect = false;
  }

  close() {
    this.skipReconnect = true;
    this.ws?.close();
  }

  send({ command, identifier, data }: ActionCableMessage) {
    if (!this.connected) {
      this.queue.add({ command, identifier, data });
      this.open();
      return;
    }
    this.ws?.send(
      JSON.stringify({
        command,
        identifier: JSON.stringify(identifier),
        data: JSON.stringify(data),
      })
    );
  }

  // shortcut for cable command subscribe
  subscribe(identifier: ActionCableMessage["identifier"]) {
    socket.send({ command: "subscribe", identifier });
    this.ws?.addEventListener("close", onClose);
    return () => {
      socket.send({ command: "unsubscribe", identifier });
      this.ws?.removeEventListener("close", onClose);
    };

    function onClose() {
      // queues subscribe command to be automatically sent on reconnect
      socket.send({ command: "subscribe", identifier });
    }
  }

  // shortcut for listening an event once
  once(action: string, listener: Listener) {
    const off = this.on(action, (data) => {
      listener(data);
      off();
    });
  }

  on(action: string, listener: Listener) {
    this.open();
    const handler = (e: MessageEvent) => {
      const data = JSON.parse(e.data);
      const message = data.message;

      if (message?.action === action) {
        listener(message.data, data);
        return;
      }

      if (message?.type === action) {
        listener(message, data);
        return;
      }

      if (data.type === action) {
        listener(data, data);
      }
    };
    this.listeners.set(listener, handler);

    // unsubscribe
    return () => {
      this.listeners.delete(listener);

      // close if there's no listeners
      if (!this.listeners.size) {
        this.close();
      }
    };
  }

  channels = {
    chats: (room?: string) => ({ channel: "ChatsChannel", room }),
    video: (room: string) => ({ channel: "VideoChannel", room }),
    patientSimulation: (userTestId: string) => ({
      channel: "PatientSimulationChannel",
      user_test_id: userTestId,
    }),
  };
}

export const socket = new Socket(`${import.meta.env.VITE_API_URL}/ws`, {
  autoConnect: false,
});

interface ActionCableMessage {
  command: string;
  identifier?: { channel: string } & Record<string, string | undefined>;
  data?: unknown;
}
